import { create } from 'zustand';
import { produce } from 'immer';
import { AquaticResourceMismatchIds, Geometry } from '@api/services/aquatic-resources/types';

type ArGeometries = {
  [key: number | string]: Geometry;
};

interface AquaticResourcesState {
  aquaticResourceDuplicateWaterNameIds: AquaticResourceMismatchIds;
  aquaticResourceGeometries: ArGeometries;
  aquaticResourceGeometryBoundaryFailureIds: AquaticResourceMismatchIds;
  aquaticResourceGeometryBoundaryInvalidPages: number[];
  aquaticResourceMismatchIds: AquaticResourceMismatchIds;
  aquaticResourceJdTypeMismatchIds: AquaticResourceMismatchIds;
  aquaticResourceJdTypes: string[];

  getAquaticResourceGeometries: () => ArGeometries;

  setAquaticResourceDuplicateWaterNames: (aquaticResourceDuplicateWaterNameIds: AquaticResourceMismatchIds) => void;
  setAquaticResourceGeometry: (arId: number | string, geometry: Geometry) => void;
  setAquaticResourceGeometryBoundaryFailures: (
    aquaticResourceGeometryBoundaryFailureIds: AquaticResourceMismatchIds
  ) => void;
  setAquaticResourceGeometryBoundaryInvalidPages: (invalidPages: number[]) => void;
  setAquaticResourceMismatches: (aquaticResourceMismatchIds: AquaticResourceMismatchIds) => void;
  setAquaticResourceJdTypeMismatchIds: (ids: AquaticResourceMismatchIds) => void;
  setAquaticResourceJdTypes: (jdTypes: string[]) => void;

  addAquaticResourceMismatches: (aquaticResourceMismatchIds: AquaticResourceMismatchIds) => void;
  clearAquaticResourceGeometryBoundaryValidation: () => void;
  clearAquaticResourceMismatches: () => void;
  removeAquaticResourceDuplicateWaterName: (aquaticResourceId: AquaticResourceMismatchIds) => void;
  removeAquaticResourceGeometry: (arId: number | string) => void;
  removeAquaticResourceMismatch: (aquaticResourceMismatchIds: AquaticResourceMismatchIds) => void;
  resetToDefaults: () => void;
}

export const useAquaticResources = create<AquaticResourcesState>((set, get) => {
  const defaults: AquaticResourcesState = {
    aquaticResourceDuplicateWaterNameIds: [],
    aquaticResourceGeometries: {},
    aquaticResourceGeometryBoundaryFailureIds: [],
    aquaticResourceGeometryBoundaryInvalidPages: [],
    aquaticResourceMismatchIds: [],
    aquaticResourceJdTypeMismatchIds: [],
    aquaticResourceJdTypes: [],

    getAquaticResourceGeometries: () => {
      return get().aquaticResourceGeometries;
    },

    setAquaticResourceDuplicateWaterNames: (aquaticResourceDuplicateWaterNameIds: AquaticResourceMismatchIds) => {
      set({ aquaticResourceDuplicateWaterNameIds });
    },

    setAquaticResourceGeometry: (arId, geometry: Geometry) => {
      set(
        produce((draft) => {
          draft.aquaticResourceGeometries[arId] = geometry;
        })
      );
    },

    setAquaticResourceGeometryBoundaryFailures: (
      aquaticResourceGeometryBoundaryFailureIds: AquaticResourceMismatchIds
    ) => {
      set({ aquaticResourceGeometryBoundaryFailureIds });
    },

    setAquaticResourceGeometryBoundaryInvalidPages: (invalidPages: number[]) => {
      set({ aquaticResourceGeometryBoundaryInvalidPages: invalidPages });
    },

    setAquaticResourceMismatches: (aquaticResourceMismatchIds: AquaticResourceMismatchIds) => {
      set({ aquaticResourceMismatchIds });
    },

    setAquaticResourceJdTypeMismatchIds: (ids: AquaticResourceMismatchIds) => {
      set({ aquaticResourceJdTypeMismatchIds: ids });
    },

    setAquaticResourceJdTypes: (jdTypes: string[]) => {
      set({ aquaticResourceJdTypes: jdTypes });
    },

    addAquaticResourceMismatches: (addedMismatches: AquaticResourceMismatchIds) => {
      const currentMismatches = get().aquaticResourceMismatchIds;

      const mismatchesToAdd: AquaticResourceMismatchIds = addedMismatches.filter(
        (newMismatch) => !currentMismatches.some((existingMismatch) => existingMismatch === newMismatch)
      );

      if (mismatchesToAdd.length === 0) {
        return;
      }

      set({
        aquaticResourceMismatchIds: [...currentMismatches, ...mismatchesToAdd],
      });
    },

    clearAquaticResourceGeometryBoundaryValidation: () => {
      set({
        aquaticResourceGeometryBoundaryFailureIds: [],
        aquaticResourceGeometryBoundaryInvalidPages: [],
      });
    },

    clearAquaticResourceMismatches: () => {
      set({
        aquaticResourceMismatchIds: [],
      });
    },

    removeAquaticResourceGeometry: (arId: number | string) => {
      set(
        produce((draft) => {
          delete draft.aquaticResourceGeometries[arId];
        })
      );
    },

    removeAquaticResourceMismatch: (aquaticResourceMismatch: AquaticResourceMismatchIds) => {
      const currentMismatches = get().aquaticResourceMismatchIds;

      const filteredMismatches = currentMismatches.filter((mismatch) => mismatch !== aquaticResourceMismatch[0]);

      set({
        aquaticResourceMismatchIds: filteredMismatches,
      });
    },

    removeAquaticResourceDuplicateWaterName: (aquaticResourceId: AquaticResourceMismatchIds) => {
      const currentDuplicateIds = get().aquaticResourceDuplicateWaterNameIds;

      set({
        aquaticResourceDuplicateWaterNameIds: currentDuplicateIds.filter((id) => id !== aquaticResourceId[0]),
      });
    },

    resetToDefaults: () => {
      set(defaults);
    },
  };

  return defaults;
});
